import { createRouter, createWebHistory } from 'vue-router'
import userStore from '@/store/UserStore'
import { downloadPageGuard } from '@/helper/RouteGuards'
import KeycloakService from '@/services/KeycloakService'
import { activateConsentManager, activateHotjar, activateInstana, activateTagManager, tagUserStore } from '@/helper/analytics'
import { setFavicon } from '@/helper/tools'

import DownloadPage from '@/views/DownloadPage.vue'
import LoginFailPage from '@/views/LoginFailPage.vue'
import ConfigSelectPage from '@/views/admin/ConfigSelectPage.vue'
import ConfigEditPage from '@/views/admin/ConfigEditPage.vue'
import TranslationEditPage from '@/views/admin/TranslationEditPage.vue'
import StepsView from '@/views/StepsView.vue'
import NewsEditPage from '@/views/admin/NewsEditPage.vue'
import ProjectOverviewPage from '@/views/ProjectOverviewPage.vue'
import { psIntegration } from '@/services/PsIntegrationService.ts'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior() {
    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      component: ProjectOverviewPage,
      name: 'ProjectOverviewPage'
    },
    {
      path: '/login-failed',
      component: LoginFailPage,
      name: 'LoginFailPage'
    },
    {
      path: '/project/:projectId',
      component: StepsView,
      name: 'project'
    },
    {
      path: '/project/:projectId/download',
      component: DownloadPage,
      name: 'DownloadPage',
      beforeEnter: [downloadPageGuard]
    },
    {
      path: '/configuration',
      component: ConfigSelectPage,
      name: 'ConfigSelectPage'
    },
    {
      path: '/configuration/nscconfig/:brand/:countryCode',
      component: ConfigEditPage,
      name: 'ConfigEditPage'
    },
    {
      path: '/configuration/translation/:brand/:locale',
      component: TranslationEditPage,
      name: 'TranslationEditPage'
    },
    {
      path: '/configuration/nscnews/:brand/:countryCode',
      component: NewsEditPage,
      name: 'NewsEditPage'
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/'
    }
  ]
})

router.beforeEach(async (to) => {
  if (to.name === 'LoginFailPage') {
    return true
  }

  if (userStore.isConfigurationLoaded) {
    return true
  }

  const keycloakService = new KeycloakService()
  return keycloakService
    .init()
    .then(async () => {
      // ?showTranslationKeys
      if (to.query.showTranslationKeys !== undefined) {
        userStore.showTranslationKeys = true
      }

      // ?brand=VAILLANT
      if (to.query.brand) {
        userStore.forcedBrand = to.query.brand as string
      }

      // ?country=XX
      if (to.query.country) {
        userStore.forcedCountry = to.query.country as string
      }

      // ?companyIdSuffix=abc
      if (to.query.companyIdSuffix !== undefined) {
        userStore.companyIdSuffix = to.query.companyIdSuffix as string
      }

      // ?token=<JWT>
      if (to.query.token) {
        userStore.token = to.query.token as string
        return { name: 'ProjectOverviewPage' }
      }

      tagUserStore()

      return userStore
        .loadConfig()
        .then(async () => {
          activateConsentManager()
          activateInstana()
          activateTagManager()
          activateHotjar()
          setFavicon(userStore.brand)

          if (to.query['ps-integration-token'] && to.name !== 'project') {
            userStore.planningSuiteToken = to.query['ps-integration-token'] as string
            const planningSuiteImportProject = await psIntegration.planningSuiteImportProject()

            return { name: 'project', params: { projectId: planningSuiteImportProject } }
          }

          return true
        })
        .catch(err => {
          console.error('Failed to load config', err)
          return { name: 'LoginFailPage' }
        })
    })
    .catch(err => {
      console.error('Failed to initialize keycloak', err)
      return { name: 'LoginFailPage' }
    })
})

export default router
